import { isIRA } from '../../IRA/IRA-utility';
import {
  AGREEMENT_TYPES,
  APP_MINIMUM_BALANCE,
  FundNoteExchange,
  MONEY_MARKET_EVENT_FEE,
  MONEY_MARKET_EVENT_GATE,
  MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
  SystemCode,
} from '../../../settings/settings';
import {
  checkFoundFeaturesByCode,
  DISABLE_ON_MATCH,
  hasRequiredServiceFeatures,
  hasZeroBalance,
  isClosedFund,
  isRolloverOrTraditionalIRA,
  updateFundNote,
} from '../shared/shared-fund-notes-utility';
import { isRetirementFund } from '../funds-utility';
import { isAPP } from '../../advice/advice-utility';
import { isCollegeContribute } from '../../transactions-util';

/**
 * Creates function for looking up exchange specific server codes
 * @param {UserFund} fund
 * @param {Number} serviceCode
 * @returns {boolean}
 */
function checkFoundFeaturesByCodeForExchange(fund, serviceCode) {
  return checkFoundFeaturesByCode(fund, serviceCode, true);
}

/**
 * Adds fund notes for exchange from funds.
 *
 * @param {ExchangeFund} fund
 * @returns {ExchangeFund}
 */
export function updateExchangeFromFundNotesForFund(fund) {
  // Zero balance
  updateFundNote(
    fund,
    fund.balanceAsOf === 0, // balance includes uncollected shares
    FundNoteExchange.FROM_ZERO,
    DISABLE_ON_MATCH
  );

  // ESA Plan
  updateFundNote(
    fund,
    fund.agreementType === 'EDUCATION_SAVINGS_ACCOUNT',
    FundNoteExchange.ESA_PLAN,
    DISABLE_ON_MATCH
  );

  // Transaction Pending and not disabled
  updateFundNote(
    fund,
    fund.hasPendingTransaction && fund.redeemableFundDollars > 0,
    FundNoteExchange.PENDING
  );

  // Transaction Pending
  updateFundNote(
    fund,
    fund.hasPendingTransaction && fund.redeemableFundDollars === 0,
    FundNoteExchange.PENDING_TRANSACTION,
    DISABLE_ON_MATCH
  );

  // Address Undeliverable
  updateFundNote(
    fund,
    checkFoundFeaturesByCode(fund, SystemCode.STOP_MAIL_ID) &&
      !fund.addressChangedSameDay,
    FundNoteExchange.ADDRESS_UNDELIVERABLE,
    DISABLE_ON_MATCH
  );

  // MMR - Redemption Block
  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_GATE &&
      fund.moneyMarketReformMessagePeriod ===
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteExchange.MONEY_MARKET_EVENT_GATE_ACTIVE,
    DISABLE_ON_MATCH
  );

  // MMR - Liquidity Fee
  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_FEE &&
      fund.moneyMarketReformMessagePeriod ===
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteExchange.MONEY_MARKET_EVENT_FEE_ACTIVE
  );

  // MMR - Redemption Block Removed
  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_GATE &&
      fund.moneyMarketReformMessagePeriod !==
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteExchange.MONEY_MARKET_EVENT_GATE_REMOVED
  );

  // MMR - Liquidity Fee Removed
  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_FEE &&
      fund.moneyMarketReformMessagePeriod !==
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteExchange.MONEY_MARKET_EVENT_FEE_REMOVED
  );

  // Stop Transfer
  updateFundNote(
    fund,
    checkFoundFeaturesByCode(fund, SystemCode.STOP_TRANSFER_ID),
    FundNoteExchange.STOP_TRANSFER,
    DISABLE_ON_MATCH
  );

  // Dividend Check (srv_id = 18 or srv_id = 17)
  updateFundNote(
    fund,
    checkFoundFeaturesByCode(fund, SystemCode.DIVIDEND_MOVE) ||
      checkFoundFeaturesByCode(
        fund,
        SystemCode.DIVIDEND_CHECK_TO_ALTERNATIVE_ADDRESS
      ),
    FundNoteExchange.WITHIN_CURRENT_INVESTMENTS
  );

  // ACH Dividend Service Feature (srv_id = 47)

  updateFundNote(
    fund,
    checkFoundFeaturesByCode(fund, SystemCode.ACH_DIVIDEND),
    FundNoteExchange.ACH_DIVIDEND
  );

  // Unavailble for online redemption
  updateFundNote(
    fund,
    false, // missingRedemptionFeature(fund, deliveryMethod) === 'all',
    FundNoteExchange.UNAVAILABLE_FOR_REDEMPTION_ONLINE,
    DISABLE_ON_MATCH
  );

  // Missing required service features
  updateFundNote(
    fund,
    !hasRequiredServiceFeatures(fund),
    FundNoteExchange.UNAVAILABLE,
    DISABLE_ON_MATCH
  );

  // Return Mail - verify address
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.RETURN_MAIL_VERIFY_ADDRESS
    ),
    FundNoteExchange.RETURN_MAIL_VERIFY_ADDRESS,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop All
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_ALL
    ),
    FundNoteExchange.AUTOMATED_SYSTEMS_STOP_ALL,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop Exchange
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_EXCHANGE
    ),
    FundNoteExchange.FROM_AUTOMATED_SYSTEMS_STOP_EXCHANGE,
    DISABLE_ON_MATCH
  );

  // Penalty Withholding
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(fund, SystemCode.PENALTY_WITHOLDING),
    FundNoteExchange.PENALTY_WITHHOLDING,
    DISABLE_ON_MATCH
  );

  // Existing iClass
  updateFundNote(
    fund,
    !fund.newFund && fund.iClass,
    FundNoteExchange.EXISTING_ICLASS
  );

  //is APP fund with less than 10K balance
  updateFundNote(
    fund,
    isAPP(fund.productType) &&
      fund.balanceAsOf > 0 &&
      fund.balanceAsOf <= APP_MINIMUM_BALANCE,
    FundNoteExchange.INELIGIBLE_BALANCE
  );
}

/**
 * Adds fund notes for exchange to funds.
 *
 * @param {ExchangeFund} fund
 * @returns {ExchangeFund}
 */
export function updateExchangeToFundNotesForFund(
  fund,
  personalData,
  exchangeFromFirstFund,
  bucketRoll = false,
  selectedFund
) {
  const {
    household: {
      householdBalanceTierStatus: { householdAssetTier = {} } = {},
    } = {},
  } = personalData;

  // Bucket roll note
  updateFundNote(
    fund,
    isIRA(fund) && bucketRoll && !isRetirementFund(exchangeFromFirstFund),
    FundNoteExchange.BUCKET_ROLL,
    DISABLE_ON_MATCH
  );

  // Closed Fund, not open to subsequent investment
  // Do not display fund note, or disable fund if it is closed allows subsequent investments
  updateFundNote(
    fund,
    isClosedFund(fund) &&
      hasZeroBalance(fund) &&
      !isCollegeContribute() &&
      fund.agreementType !== 'COLLEGE_SAVINGS' &&
      !householdAssetTier.closedFundAccess,
    FundNoteExchange.CLOSED_NEW,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop All
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_ALL
    ),
    FundNoteExchange.AUTOMATED_SYSTEMS_STOP_ALL,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop Balance
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_BALANCE
    ),
    FundNoteExchange.AUTOMATED_SYSTEMS_STOP_BALANCE,
    DISABLE_ON_MATCH
  );

  // Autmoated Systems Stop Check
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_CHECK
    ),
    FundNoteExchange.AUTOMATED_SYSTEMS_STOP_CHECK,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop Purchase
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_PURCHASE
    ),
    FundNoteExchange.AUTOMATED_SYSTEMS_STOP_PURCHASE,
    DISABLE_ON_MATCH
  );

  // Automated Systems Stop Exchange
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_EXCHANGE
    ),
    FundNoteExchange.TO_AUTOMATED_SYSTEMS_STOP_EXCHANGE,
    DISABLE_ON_MATCH
  );

  // Return Mail - Verify Address
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(
      fund,
      SystemCode.RETURN_MAIL_VERIFY_ADDRESS
    ),
    FundNoteExchange.RETURN_MAIL_VERIFY_ADDRESS,
    DISABLE_ON_MATCH
  );

  // Stop Purchase
  updateFundNote(
    fund,
    checkFoundFeaturesByCodeForExchange(fund, SystemCode.STOP_PURCHASE),
    FundNoteExchange.STOP_PURCHASE,
    DISABLE_ON_MATCH
  );
  // Purchase Block (excessive trader block)
  updateFundNote(
    fund,
    fund.purchaseBlocked &&
      fund.agreementType !== AGREEMENT_TYPES.TRADITIONAL_IRA_DECEASED_BENE &&
      fund.agreementType !== AGREEMENT_TYPES.ROTH_IRA_DECEASED_BENE,
    FundNoteExchange.PURCHASED_BLOCKED,
    DISABLE_ON_MATCH
  );

  // If client has a recent redemption of a fund within the past 30 days, "Recently Redeemed. Available on or after MM/YY"
  // will be added to the notes section. (Date is 30 days after the redemption date).
  updateFundNote(
    fund,
    fund.purchaseBlocked && fund.purchaseBlockRemovalDate,
    FundNoteExchange.RECENTLY_REDEEMED,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    !fund.newFund &&
      !fund.canExchange &&
      !(
        selectedFund &&
        isRolloverOrTraditionalIRA(selectedFund) &&
        fund.agreementType === 'ROTH_IRA'
      ),
    FundNoteExchange.NOT_ELIGIBLE,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    selectedFund &&
      isRolloverOrTraditionalIRA(selectedFund) &&
      fund.agreementType === 'ROTH_IRA',
    FundNoteExchange.REQUIRES_CONVERSION,
    DISABLE_ON_MATCH
  );

  //is APP fund with zero dollor balance
  updateFundNote(
    fund,
    isAPP(fund.productType) && fund.balanceAsOf <= 0,
    FundNoteExchange.EXCHANGE_UNAVAILABLE,
    DISABLE_ON_MATCH
  );

  //Fund is merged
  updateFundNote(
    fund,
    fund.merged && fund.balanceAsOf <= 0,
    FundNoteExchange.EXCHANGE_UNAVAILABLE,
    DISABLE_ON_MATCH
  );

  // Existing iClass
  updateFundNote(
    fund,
    !fund.newFund && fund.iClass,
    FundNoteExchange.EXISTING_ICLASS
  );
}
